import {
  Component,
  Input,
  ViewEncapsulation,
  OnChanges,
  ElementRef,
  ViewChild,
  AfterViewInit,
  SimpleChange
} from '@angular/core';
import { Form, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LoadingStateService } from '../../../providers/loading-state/loading-state.service';
import { ValueAccessorBase } from '../default-class/value-accessor';
import { InputMaskDataModel } from './input-mask-data.model';

@Component({
  selector: 'tidy-input',
  templateUrl: 'input.component.html',
  styleUrls: ['./input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: InputComponent,
    multi: true,
  }],
})

export class InputComponent extends ValueAccessorBase<any> implements OnChanges, AfterViewInit {
  @ViewChild('maskedInput', {static: false}) maskedInputRef: ElementRef;
  // Mean normal input
  @ViewChild('neurotypicalInput', {static: false}) neurotypicalInput: ElementRef;

  @Input() public label: string;
  @Input() public placeholder: string;
  @Input() public icon: string;
  @Input() public activeIcon: string;
  @Input() public form: FormControl;
  @Input() public formControlName: any;
  @Input() public formControl: any;
  @Input() public handleUpdatesOnKeyUp: boolean = false;
  @Input() public mask: string;
  @Input() public currencySymbol: string;
  @Input() public submitted: boolean;
  @Input() public errorMessage: string;
  @Input() public type: string;
  @Input() public noBorder: boolean;
  @Input() public inputMode: string;
  @Input() autoComplete: string;
  @Input() customMask: InputMaskDataModel;
  @Input() unmask: boolean;
  @Input() action = null;
  @Input() inputId: string;
  @Input() textSuffix: string;
  @Input() buttonIcon: string;
  @Input() buttonIconColor = 'white';
  actionLoading: boolean;
  showSideButton: boolean = false;

  focused = false;
  touchedState = false;
  inputMask: InputMaskDataModel;
  showCurrencyCharacter = false;
  @Input() isInputEmpty = true;

  constructor(
    public loadingState: LoadingStateService
  ) {
    super();
   }

  ngAfterViewInit() {
    if (this.inputMask && this.inputMask.mask && this.maskedInputRef) {
      const keyupNativeEvent = new Event('keyup');
      setTimeout(_ => {
        // To show input masked on the reload of the page
        this.maskedInputRef.nativeElement.dispatchEvent(keyupNativeEvent);
        // call blur() to prevent input focus on ios devices
        this.maskedInputRef.nativeElement.blur();
      }, 0);
    }
    if (this.form?.value && this.form?.value?.length > 0) {
      this.isInputEmpty = false;
    }
  }

  public focusNativeInput() {
    if (this.inputMask && this.inputMask.mask && this.maskedInputRef) {
      this.maskedInputRef.nativeElement.focus();
    } else if (this.neurotypicalInput) {
      // On ios it is not possible to handle keyboard open and close using capacitor
      // Because that is necessary to use focus() and blur()
      this.neurotypicalInput.nativeElement.focus();
    }
  }

  public hideNativeInput() {
    if (this.inputMask && this.inputMask.mask && this.maskedInputRef) {
      this.maskedInputRef.nativeElement.blur();
    } else if (this.neurotypicalInput) {
      // On ios it is not possible to handle keyboard open and close using capacitor
      // Because that is necessary to use focus() and blur()
      this.neurotypicalInput.nativeElement.blur();
    }
  }

  changeTouch = () => {
    this.touchedState = true;
  }

  keyUpHandle(event) {
    this.isInputEmpty = event.target.value === '';
    if (this.handleUpdatesOnKeyUp) {
      this.form.patchValue(this.value);
      this.form.updateValueAndValidity();
      this.touch();
    }
  }

  getMask(): InputMaskDataModel {
    const masks = {
      phone: {
        maxLenghth: 14,
        mask: '(000) 000-0000',
        additionalCharacter: null
      },
      currencyMask: {
        lazy: true,
        mask: this.currencySymbol ? `${this.currencySymbol}num` : '$num',
        blocks: {
          num: {
            mask: Number,
            scale: 2,
            signed: false,
            thousandsSeparator: ',',
            padFractionalZeros: true,
            normalizeZeros: true,
            radix: '.',
            mapToRadix: ['.']
          }
        }
      },
      zipcode: {
        maxLenghth: 5,
        mask: '00000',
        additionalCharacter: null
      },
      number: {
        lazy: false,
        mask: 'num',
        blocks: {
          num: {
            mask: Number,
          }
        }
      }
    };
    return masks[this.mask];
  }

  ngOnChanges(changes: { customMask?: SimpleChange, mask?: SimpleChange, form?: SimpleChange }) {
    if (changes.customMask) {
      this.inputMask = this.customMask;
    }

    if (changes.mask) {
      this.inputMask = this.getMask();
    }

    if (changes.form) {
      const newForm = changes.form.currentValue;
      if (newForm) {
        this.form = newForm;
        this.value = newForm.value;
      }
    }
  }

  setFocused(status) {
    this.focused = status;
  }

  async executeAction() {
    this.errorMessage = ''
    if (this.loadingState.loading || !this.action) {
      return;
    }

    try {
      this.loadingState.loading = true;
      this.actionLoading = true;
      await this.action();
      if (!this.form.value || this.form.value.length === 0) {
        this.isInputEmpty = true;
      }
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    } finally {
      this.loadingState.loading = false;
      this.actionLoading = false;
    }
  }
}
